import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AsyncPaginate } from "react-select-async-paginate";
import { getNodes } from "../api/quontxApi";
import { resetState, loadFromState, saveToState } from "../utils";
import ParquetTable from "./ParquetTable";

const ITEMS_PER_PAGE = 25;

const HEADER_NAME_MAP = {
  'ti': 'Time Interval',
  'similarity': 'Similarity',
  'norm_delta_pct': 'Norm Delta %',
  'uuid_1': 'UUID 1',
  'uuid_2': 'Organisation',
  'label_2': 'Org'
};

const COLUMNS_TO_HIDE = ['uuid_1', 'uuid_2', 'label_1'];
const DEFAULT_SORTED_COLUMN = 'similarity';

const ProfileComparator1_NFilters = ({
  isLoading,
  propagnateData,
  fetchDataFunction, // ADD THIS PROP
}) => {
  const propagnateFunctionRef = useRef(propagnateData);
  const fetchDataFunctionRef = useRef(fetchDataFunction); // ADD THIS

  const [nodeA, setNodeA] = useState(null);
  const [tableKey, setTableKey] = useState(0);

  // Keep fetchDataFunction ref updated
  useEffect(() => {
    fetchDataFunctionRef.current = fetchDataFunction;
  }, [fetchDataFunction]);

  const resetFilters = () => {
    resetState("profileComparatorState", [
      { stateFunc: setNodeA, nullVal: null },
    ]);
    setTableKey(prev => prev + 1);
  };

  useEffect(() => {
    loadFromState("profileComparatorState", [
      { stateFunc: setNodeA, storageObjectKey: "nodeA", defaultVal: null },
    ]);

    const propagnateDataFunctionCopy = propagnateFunctionRef.current;
    return () => {
      propagnateDataFunctionCopy({});
    };
  }, []);

  // ---------------------------
  // Server-side node search
  // ---------------------------

  const toOption = (node) => ({
    label: node.name,
    value: node.uuid,
  });

  const fetchNodesPage = useCallback(async ({ search, page }) => {
    const res = await getNodes({
      node_type: "NAMED_ENTITY:ORGANISATION",
      name: search,
      page,
      itemsPerPage: ITEMS_PER_PAGE,
    });

    return res?.data?.data ?? [];
  }, []);

  const loadNodeOptions = useCallback(
    async (search, loadedOptions, additional) => {
      const q = (search || "").trim();
      if (q.length < 2) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }

      const page = additional?.page ?? 1;
      const items = await fetchNodesPage({ search: q, page });

      const options = items.map(toOption);
      const hasMore = items.length === ITEMS_PER_PAGE;

      return {
        options,
        hasMore,
        additional: { page: page + 1 },
      };
    },
    [fetchNodesPage]
  );

  const nodeAdditional = useMemo(() => ({ page: 1 }), []);

  // ---------------------------
  // Fetch data function for ParquetTable
  // ---------------------------

  const fetchTableData = useCallback(async () => {
    return await fetchDataFunctionRef.current({
      nodeA: nodeA.value,
    });
  }, [nodeA]);

  // ---------------------------
  // Save state when node changes
  // ---------------------------

  useEffect(() => {
    if (nodeA !== null) {
      saveToState("profileComparatorState", { nodeA });
    }
  }, [nodeA]);

  // Determine if table should load
  const shouldLoadTable = nodeA?.value;

  // Callback when data is loaded (optional - for propagating data)
  const handleDataLoaded = useCallback((data) => {
    if (propagnateFunctionRef.current) {
      propagnateFunctionRef.current(data);
    }
  }, []);

  return (
    <div className="w-full flex flex-col items-center content-center">
      {isLoading && <p>Loading data...</p>}

      {!isLoading && (
        <div className="w-full flex justify-center">
          <div className="w-full max-w-6xl flex flex-col gap-8">
            {/* ROW 1: Node Selector */}
            <div className="flex flex-col gap-2 items-start">
              <p>Node</p>
              <div className="w-[320px]">
                <AsyncPaginate
                  value={nodeA}
                  onChange={(selected) => setNodeA(selected)}
                  loadOptions={loadNodeOptions}
                  additional={nodeAdditional}
                  debounceTimeout={250}
                  cacheOptions
                  placeholder="Type to search…"
                  noOptionsMessage={({ inputValue }) =>
                    (inputValue || "").trim().length < 2
                      ? "Type at least 2 characters"
                      : "No matches"
                  }
                />
              </div>
            </div>

            {/* ROW 2: Reset Button */}
            <div className="flex items-start">
              <button
                onClick={resetFilters}
                className="bg-blue-500 text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200"
              >
                Reset filters
              </button>
            </div>

            {/* No Data Message */}
            {!nodeA && (
              <p className="text-sm opacity-70">
                Select a node to view profile comparison data.
              </p>
            )}

            {/* Table */}
            <ParquetTable
              key={tableKey}
              fetchData={fetchTableData}
              headerNameMap={HEADER_NAME_MAP}
              columnsToHide={COLUMNS_TO_HIDE}
              defaultSortedColumn={DEFAULT_SORTED_COLUMN}
              shouldLoad={shouldLoadTable}
              onDataLoaded={handleDataLoaded}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileComparator1_NFilters;